import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle, BellRing, CheckCheck, CheckCircle2, ExternalLink, Eye, Laptop,
  ShieldCheck, SlidersHorizontal, Wrench,
} from 'lucide-react'
import { Drawer, ToastProvider, useToast, apiGet, apiMutate, adminMutate, usePermissions } from './primitives.js'
import { navigate } from '../router.js'
import { useNowMinute } from '../soirees/lib.js'
import {
  ACTIVITY_FILTERS, TOUTES_FAMILLES, activityFeed, applyFilter, attentionCount, famillesDuFlux, filtrerParFamille,
  groupByDay, lienSonde, pinnedAlerts, severityLabel,
  type ActivityFilter, type AlerteSource, type FeedItem, type NotificationSource, type PinnedItem,
} from './activity-model.js'
import { RattachementsAVenirPanel } from '../soirees/Rattachements.js'
import { BoutonActualiser } from '../supervision/vues/actualiser.js'
import { alerteSondeDe } from '../supervision/vues/actualiser-modele.js'
import { renommerSalles } from '../supervision/vues/salles.js'
import '../notifications.css'

/**
 * CENTRE D'ACTIVITÉ — /activite
 *
 * UNE destination pour tout ce que Gestion signale. Avant, il y en avait
 * deux : « Alertes » dans le menu, « Notifications » derrière un bouton de la
 * page Alertes. Deux endroits pour la même phrase — « quelque chose mérite
 * ton attention » — et aucun qui faisait autorité.
 *
 * L'écran est en DEUX TEMPS, et l'ordre n'est pas décoratif :
 *
 *   À TRAITER          les alertes encore vraies, épinglées. Elles ne
 *                      descendent jamais dans le flux : une alerte de six
 *                      heures compte PLUS qu'une de six minutes, pas moins.
 *   ACTIVITÉ RÉCENTE   ce qui s'est passé, daté, du plus récent au plus
 *                      ancien — notifications reçues et alertes résolues.
 *
 * La fusion est une fusion d'EXPÉRIENCE. Les deux modèles métier restent
 * séparés côté serveur, avec leurs API, leurs permissions et leurs actions —
 * voir `activity-model.ts`.
 */

interface InstanceDetail {
  id: string; rule_id: string | null; rule_name: string
  service_slug: string; service_name: string; metric_label: string
  severity: string; started_at: string; fired_at: string
  acknowledged_at: string | null; acknowledged_by_name: string | null
  resolved_at: string | null; resolution_reason: string | null
  state: string; duration: string; observed: string | null; normal_since: string | null
  communications: { channel: string; status: string; recipient: string; skip_reason: string | null; error_class: string | null; at: string }[]
}

const CH_LABEL: Record<string, string> = { IN_APP: 'In-App', EMAIL: 'E-mail', PUSH: 'Push' }
const DSTATUS: Record<string, string> = {
  QUEUED: 'En file', PROCESSING: 'En cours', ACCEPTED: 'Accepté', DELIVERED: 'Délivré',
  FAILED: 'Échec', SKIPPED: 'Ignoré', DEFERRED: 'Préparé (mobile non connecté)',
}
const RESOLUTION: Record<string, string> = {
  AUTO_RECOVERED: 'automatique (retour à la normale)', MANUAL: 'manuelle',
  RULE_DISABLED: 'règle désactivée', RULE_DELETED: 'règle supprimée',
  // Source confiée à Sonde : c'est elle qui alerte désormais (21/10/2026).
  TRANSFERRED_TO_SONDE: 'transférée à Sonde',
}

/**
 * UN 404 N'EST PAS UN INCIDENT.
 *
 * `apiGet` RÉSOUT sur une erreur HTTP : elle rend le corps `{ error, message }`
 * sans lever. Poser ce corps comme « détail » ouvrait le tiroir sur un objet
 * vide, et la lecture de `communications.length` faisait tomber toute la page
 * — écran noir sur un lien profond périmé, ce qui est précisément le cas où
 * l'on a le plus besoin de lire quelque chose.
 *
 * On vérifie donc que la réponse EST un incident avant de l'afficher.
 */
function estIncident(d: unknown): d is InstanceDetail {
  return !!d && typeof d === 'object' && Array.isArray((d as InstanceDetail).communications)
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}
function heure(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}
/** « Depuis 18 min » — l'ancienneté d'un problème encore ouvert. */
function depuis(iso: string, nowMs: number): string {
  const s = Math.max(0, (nowMs - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'à l’instant'
  if (s < 3600) return `${Math.floor(s / 60)} min`
  if (s < 86400) return `${Math.floor(s / 3600)} h`
  return `${Math.floor(s / 86400)} j`
}
function tint(s: string): string {
  if (s === 'CRITICAL' || s === 'ERROR') return 'critical'
  if (s === 'ACTION_REQUIRED') return 'action'
  if (s === 'RECOVERY' || s === 'SUCCESS') return 'ok'
  return 'info'
}
function IconePour({ a }: { a: AlerteSource }) {
  if (a.origin === 'RULE') return <BellRing size={18} />
  if (a.service_key === 'main-courante') return <ShieldCheck size={18} />
  if (a.service_key === 'parc') return <Laptop size={18} />
  return <AlertTriangle size={18} />
}

/* ═══════════════════════════ ÉPINGLÉES ═══════════════════════════ */

function PinnedRow({ p, nowMs, onOpen }: { p: PinnedItem; nowMs: number; onOpen: () => void }) {
  return (
    <li className={`ac-alert ac-alert--${tint(p.severity)}`}>
      <span className="ac-alert__stripe" aria-hidden="true" />
      <span className="ac-alert__icon" aria-hidden="true"><IconePour a={p.alert} /></span>
      <button type="button" className="ac-alert__main" onClick={onOpen}>
        <span className="ac-alert__title">{p.title}</span>
        <span className="ac-alert__meta">
          {/* La sévérité est ÉCRITE, pas seulement colorée (§65). */}
          <span className="ac-alert__sev">{severityLabel(p.severity)}</span>
          <span aria-hidden="true">·</span>
          <span>{p.source}</span>
          {p.acknowledged ? (
            <>
              <span aria-hidden="true">·</span>
              <span>prise en charge{p.acknowledgedBy ? ` par ${p.acknowledgedBy}` : ''}</span>
            </>
          ) : null}
          {p.note ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{p.note}</span>
            </>
          ) : null}
        </span>
      </button>
      <span className="ac-alert__age">
        <span className="ac-alert__age-v">{depuis(p.since, nowMs)}</span>
        <span className="ac-alert__age-l">ouverte depuis</span>
      </span>
    </li>
  )
}

/* ═══════════════════════════ FLUX ═══════════════════════════ */

function FeedRow({ f, onOpen }: { f: FeedItem; onOpen: () => void }) {
  const cliquable = f.deepLink !== null || f.notificationId !== null
  return (
    <li className={`ac-tl__row${f.unread ? ' ac-tl__row--unread' : ''}`}>
      <time className="ac-tl__time" dateTime={f.at}>{heure(f.at)}</time>
      <span className={`ac-tl__dot ac-tl__dot--${tint(f.severity)}`} aria-hidden="true" />
      {cliquable ? (
        <button type="button" className="ac-tl__body" onClick={onOpen}>
          <span className="ac-tl__title">{f.title}</span>
          <span className="ac-tl__meta">
            {f.source}{f.detail ? ` · ${f.detail}` : ''}{f.unread ? ' · non lue' : ''}
          </span>
        </button>
      ) : (
        <span className="ac-tl__body ac-tl__body--flat">
          <span className="ac-tl__title">{f.title}</span>
          <span className="ac-tl__meta">{f.source}{f.detail ? ` · ${f.detail}` : ''}</span>
        </span>
      )}
    </li>
  )
}

/* ═══════════════════════════ PAGE ═══════════════════════════ */

function Inner({ focusInstance }: { focusInstance: string | null }) {
  const toast = useToast()
  const { permissions, authenticated } = usePermissions()
  const canAck = authenticated && permissions.includes('alert.acknowledge')
  const canResolve = authenticated && permissions.includes('alert.resolve')
  const canRules = authenticated && permissions.includes('rule.read')

  const nowMs = useNowMinute(true)
  const [filtre, setFiltre] = useState<ActivityFilter>('all')
  // La famille (Comptabilité, Sécurité…) : un second filtre, indépendant du
  // premier — « non lues de la Comptabilité » se lit d'un coup.
  const [famille, setFamille] = useState<string>(TOUTES_FAMILLES)
  const [alerts, setAlerts] = useState<AlerteSource[]>([])
  const [notifs, setNotifs] = useState<NotificationSource[]>([])
  const [pret, setPret] = useState(false)
  const [selected, setSelected] = useState<PinnedItem | null>(null)
  const [detail, setDetail] = useState<InstanceDetail | null>(null)
  // Sonde en mode ombre : son flux est vide, ce n'est pas une panne — on le dit.
  const [ombre, setOmbre] = useState<string | null>(null)

  /*
   * DEUX SOURCES, DEUX PERMISSIONS.
   *
   * Chaque appel garde son propre contrôle d'accès : le Centre n'élargit
   * rien. Un utilisateur qui peut lire ses notifications sans administrer les
   * alertes obtient une liste d'alertes vide et un écran qui fonctionne —
   * pas une page d'erreur.
   */
  const charger = useCallback(() => {
    const a = apiGet<{ items: AlerteSource[] }>('/api/v1/alerts?state=all&limit=100')
      .then((j) => j.items).catch(() => [] as AlerteSource[])
    const n = apiGet<{ items: NotificationSource[] }>('/api/v1/notifications?limit=100&filter=all')
      .then((j) => j.items).catch(() => [] as NotificationSource[])
    void Promise.all([a, n]).then(([ia, inn]) => { setAlerts(ia); setNotifs(inn); setPret(true) })
  }, [])
  useEffect(() => {
    charger()
    // Rythme calme, aligné sur le reste du shell : pas de canal dédié.
    const t = setInterval(charger, 60_000)
    return () => clearInterval(t)
  }, [charger])

  /* L'état de la supervision : `apiGet` résout sur une erreur HTTP — on vérifie la forme. */
  useEffect(() => {
    apiGet<unknown>('/api/v1/supervision/etat')
      .then((d) => {
        const e = d as { state?: unknown; shadow_mode?: unknown; shadow_summary_fr?: unknown } | null
        if (!e || e.state !== 'ok' || e.shadow_mode !== true) { setOmbre(null); return }
        setOmbre(typeof e.shadow_summary_fr === 'string' && e.shadow_summary_fr
          ? e.shadow_summary_fr
          : 'Sonde est en mode ombre : elle observe, mais ses alertes ne sont pas encore publiées ici.')
      })
      .catch(() => setOmbre(null))
  }, [])

  /* Lien profond /activite/:id — ouvre directement l'incident. */
  useEffect(() => {
    if (!focusInstance) return
    apiGet<unknown>(`/api/v1/alerts/instances/${focusInstance}`)
      .then((d) => {
        if (estIncident(d)) { setDetail(d); return }
        toast('error', 'Cet incident n’existe plus.')
        navigate('/activite')
      })
      .catch(() => {
        toast('error', 'Cet incident n’existe plus.')
        navigate('/activite')
      })
  }, [focusInstance]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selected?.alert.origin === 'RULE' && selected.alert.instance_id) {
      apiGet<unknown>(`/api/v1/alerts/instances/${selected.alert.instance_id}`)
        .then((d) => { if (estIncident(d)) setDetail(d) }).catch(() => undefined)
    } else if (!focusInstance) setDetail(null)
  }, [selected, focusInstance])

  const pinned = useMemo(() => pinnedAlerts(alerts), [alerts])
  const feed = useMemo(() => activityFeed(alerts, notifs), [alerts, notifs])
  const familles = useMemo(() => famillesDuFlux(feed), [feed])
  // Une famille choisie qui disparaît du flux (rétention, lecture) ne laisse
  // pas l'écran filtré sur rien.
  const familleActive = famille === TOUTES_FAMILLES || familles.some((f) => f.value === famille) ? famille : TOUTES_FAMILLES
  const feedFiltre = useMemo(() => applyFilter(filtrerParFamille(feed, familleActive), filtre), [feed, familleActive, filtre])
  const groupes = useMemo(() => groupByDay(feedFiltre, nowMs), [feedFiltre, nowMs])
  const nonLues = feed.filter((f) => f.unread).length
  const aTraiter = useMemo(() => attentionCount(alerts), [alerts])

  const fermer = () => {
    setSelected(null)
    if (focusInstance) navigate('/activite')
    else setDetail(null)
  }

  const ouvrirFlux = async (f: FeedItem) => {
    if (f.notificationId && f.unread) {
      await apiMutate('POST', `/api/v1/notifications/${f.notificationId}/read`)
      charger()
    }
    if (f.deepLink) navigate(f.deepLink)
  }

  const acquitterService = async (p: PinnedItem) => {
    const r = await apiMutate<{ message?: string }>('POST', `/api/v1/alerts/${p.alert.service_key}/${encodeURIComponent(p.alert.external_alert_id)}/ack`)
    if (r.status === 200) { toast('ok', 'Alerte acquittée'); setSelected(null); charger() }
    // Sonde : son message dit ce qui s'est passé (injoignable, incident déjà résolu…).
    else toast('error', p.alert.service_key === 'sonde' && r.data?.message
      ? r.data.message
      : 'L’acquittement n’a pas été enregistré. Rien n’a changé — réessayez.')
  }
  const prendreEnCharge = async (id: string) => {
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/alerts/instances/${id}/ack`)
    if (r.status === 200) {
      toast('ok', 'Incident pris en charge — la surveillance continue.')
      apiGet<unknown>(`/api/v1/alerts/instances/${id}`)
        .then((d) => { if (estIncident(d)) setDetail(d) }).catch(() => undefined)
      charger()
    } else toast('error', r.data.message ?? 'La prise en charge a échoué.')
  }
  const resoudre = async (id: string) => {
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/alerts/instances/${id}/resolve`)
    if (r.status === 200) { toast('ok', 'Incident résolu — la règle est réarmée.'); fermer(); charger() }
    else toast('error', r.data.message ?? 'La résolution a échoué.')
  }

  const showDetail = detail !== null
  const titreDrawer = showDetail ? detail.rule_name : selected?.title ?? ''

  return (
    <main className="lc-page lc-stagger ac-page">
      <div className="adm-head">
        <div>
          <h1 className="lc-title">Centre d’activité</h1>
          <p className="lc-subtitle">
            Ce qui demande votre attention, et ce qui s’est passé.
            Une alerte est un état encore vrai ; une notification est un message reçu.
          </p>
        </div>
        <div className="adm-head__actions">
          {nonLues > 0 ? (
            <button className="lc-btn lc-btn--ghost" onClick={async () => { await apiMutate('POST', '/api/v1/notifications/read-all'); charger() }}>
              <CheckCheck size={14} aria-hidden="true" /> Tout marquer lu
            </button>
          ) : null}
          {canRules ? (
            <button className="lc-btn" onClick={() => navigate('/alerts/rules')}>
              <SlidersHorizontal size={14} aria-hidden="true" /> Règles
            </button>
          ) : null}
        </div>
      </div>

      {/* ═══ À TRAITER — épinglé, jamais filtré ═══ */}
      {pinned.length > 0 ? (
        <section className="ac-pinned" aria-labelledby="ac-pinned-h">
          <div className="so-section-head">
            <h2 className="lc-section-title" id="ac-pinned-h">
              <AlertTriangle size={16} aria-hidden="true" /> À traiter
            </h2>
            {/* Le compteur de la barre latérale ne compte QUE ce qui n'a
                pas encore été pris en charge : la différence est dite ici
                plutôt que laissée à deviner entre deux nombres. */}
            <span className="lc-hint">
              {pinned.length} point{pinned.length > 1 ? 's' : ''} d’attention
              {pinned.length - aTraiter > 0 ? ` · ${pinned.length - aTraiter} déjà pris en charge` : ''}
              {' · reste en haut tant que ce n’est pas résolu'}
            </span>
          </div>
          <ul className="ac-band">
            {pinned.map((p) => <PinnedRow key={p.key} p={p} nowMs={nowMs} onOpen={() => setSelected(p)} />)}
          </ul>
        </section>
      ) : pret ? (
        /* État vide POSITIF et compact : pas de grande zone creuse (§31). */
        <p className="gd-calm"><CheckCircle2 size={15} aria-hidden="true" />
          Aucun point d’attention actuellement
        </p>
      ) : null}

      {ombre ? <p className="lc-hint">{ombre}</p> : null}

      {/* ═══ RATTACHEMENTS ANNONCÉS (19/10/2026) — avant la nuit, corrigeables
          d'un clic ; ni une alerte (rien n'est en panne) ni un message passé. ═══ */}
      <RattachementsAVenirPanel lieu="activite" />

      {/* ═══ ACTIVITÉ RÉCENTE ═══ */}
      <section className="ac-feed" aria-labelledby="ac-feed-h">
        <div className="so-section-head so-section-head--wrap">
          <h2 className="lc-section-title" id="ac-feed-h">Activité récente</h2>
          <div className="lc-seg" role="tablist" aria-label="Filtre">
            {ACTIVITY_FILTERS.map((f) => (
              <button key={f.value} role="tab" aria-selected={filtre === f.value}
                className={`lc-seg__btn ${filtre === f.value ? 'lc-seg__btn--on' : ''}`}
                onClick={() => setFiltre(f.value)}>
                {f.label}{f.value === 'unread' && nonLues > 0 ? ` · ${nonLues}` : ''}
              </button>
            ))}
          </div>
        </div>

        {/* Le filtre par famille n'apparaît que s'il départage quelque chose. */}
        {familles.length > 1 && filtre !== 'attention' ? (
          <div className="nt-familles">
            <span className="nt-familles__label" id="ac-familles-l">Famille</span>
            <div className="lc-seg" role="tablist" aria-labelledby="ac-familles-l">
              {[{ value: TOUTES_FAMILLES, label: 'Toutes' }, ...familles].map((f) => (
                <button key={f.value} role="tab" aria-selected={familleActive === f.value}
                  className={`lc-seg__btn ${familleActive === f.value ? 'lc-seg__btn--on' : ''}`}
                  onClick={() => setFamille(f.value)}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {filtre === 'attention' ? (
          <p className="lc-hint ac-empty">
            {pinned.length > 0
              ? 'Les points d’attention sont épinglés en haut de cet écran.'
              : 'Rien à traiter — tout est calme.'}
          </p>
        ) : groupes.length === 0 ? (
          <p className="lc-hint ac-empty">
            {pret
              ? (filtre === 'unread' ? 'Aucune notification non lue.' : 'Aucune activité récente.')
                + (familleActive !== TOUTES_FAMILLES ? ` Le filtre « ${familles.find((f) => f.value === familleActive)?.label ?? familleActive} » est actif.` : '')
              : 'Chargement de l’activité'}
          </p>
        ) : groupes.map((g) => (
          <div key={g.label} className="ac-day">
            <h3 className="ac-day__label">{g.label}</h3>
            <ol className="ac-tl">
              {g.items.map((f) => <FeedRow key={f.key} f={f} onOpen={() => void ouvrirFlux(f)} />)}
            </ol>
          </div>
        ))}
      </section>

      {/* ═══ Détail — le drawer déjà en place, pas une nouvelle architecture ═══ */}
      <Drawer
        open={selected !== null || (focusInstance !== null && detail !== null)}
        title={titreDrawer}
        onClose={fermer}
        footer={showDetail ? (
          <>
            {canAck && detail.state === 'ACTIVE' ? (
              <button className="lc-btn lc-btn--primary" onClick={() => prendreEnCharge(detail.id)}>
                <Eye size={15} aria-hidden="true" /> Prendre en charge
              </button>
            ) : null}
            {canResolve && detail.state !== 'RESOLVED' ? (
              <button className="lc-btn" onClick={() => resoudre(detail.id)}>
                <Wrench size={14} aria-hidden="true" /> Résoudre
              </button>
            ) : null}
            {detail.rule_id && canRules ? (
              <button className="lc-btn lc-btn--ghost" onClick={() => navigate(`/alerts/rules/${detail.rule_id}`)}>
                Voir la règle
              </button>
            ) : null}
          </>
        ) : selected ? (
          <>
            {canAck && selected.alert.state === 'ACTIVE' && !selected.acknowledged ? (
              <button className="lc-btn lc-btn--primary" onClick={() => acquitterService(selected)}>
                <Eye size={15} aria-hidden="true" /> Acquitter
              </button>
            ) : null}
            {/* « Actualiser » (25/10/2026) : une ALERTE de Sonde encore vraie ; un incident s'actualise alerte par alerte, depuis sa fiche. */}
            {selected.alert.service_key === 'sonde' && selected.alert.state !== 'RESOLVED' && alerteSondeDe(selected.alert.external_alert_id) ? (
              <BoutonActualiser cible={{ kind: 'alert', id: alerteSondeDe(selected.alert.external_alert_id)!, nom: renommerSalles(selected.alert.title) }} apres={charger} />
            ) : null}
            {lienSonde(selected.alert) ? (
              <button className="lc-btn" onClick={() => { const l = lienSonde(selected.alert)!; setSelected(null); navigate(l) }}>
                Ouvrir dans la supervision
              </button>
            ) : selected.alert.url ? (
              <a className="lc-btn" href={selected.alert.url} target="_blank" rel="noopener noreferrer">
                Ouvrir dans {selected.alert.service_name} <ExternalLink size={14} aria-hidden="true" />
              </a>
            ) : null}
          </>
        ) : undefined}
      >
        {showDetail ? (
          <>
            <dl className="lc-kv">
              <dt>Service</dt><dd>{detail.service_name}</dd>
              <dt>Donnée</dt><dd>{detail.metric_label}{detail.observed ? ` — observé : ${detail.observed}` : ''}</dd>
              <dt>Sévérité</dt><dd>{severityLabel(detail.severity)}</dd>
              <dt>Statut</dt>
              <dd>{detail.state === 'ACTIVE' ? 'Active — la condition est toujours vraie'
                : detail.state === 'ACKNOWLEDGED' ? 'Prise en charge — la surveillance continue'
                : `Résolue (${RESOLUTION[detail.resolution_reason ?? ''] ?? detail.resolution_reason ?? '—'})`}</dd>
              {detail.normal_since && !detail.resolved_at ? (
                <><dt>Retour à la normale</dt><dd>depuis {fmtDate(detail.normal_since)} — clôture manuelle requise</dd></>
              ) : null}
              <dt>Début de l’incident</dt><dd>{fmtDate(detail.started_at)}</dd>
              <dt>Alerte déclenchée</dt><dd>{fmtDate(detail.fired_at)}</dd>
              {detail.acknowledged_at ? (
                <><dt>Prise en charge</dt><dd>par {detail.acknowledged_by_name ?? '—'} à {fmtDate(detail.acknowledged_at)}</dd></>
              ) : null}
              {detail.resolved_at ? (<><dt>Résolue</dt><dd>{fmtDate(detail.resolved_at)}</dd></>) : null}
              <dt>Durée</dt><dd>{detail.duration}</dd>
            </dl>
            <h3 className="lc-section-title" style={{ marginTop: 16 }}>Communications</h3>
            {detail.communications.length === 0 ? <p className="lc-hint">Aucune communication pour cet incident.</p> : (
              <div className="ad-comm-list">
                {detail.communications.map((c, i) => (
                  <div key={i} className="ad-comm-row">
                    <span>{CH_LABEL[c.channel] ?? c.channel}</span>
                    <span className="lc-hint">{c.recipient}</span>
                    <span className={`lc-pill ${c.status === 'ACCEPTED' || c.status === 'DELIVERED' ? 'lc-pill--ok' : c.status === 'FAILED' ? 'lc-pill--danger' : 'lc-pill--idle'}`}>
                      {DSTATUS[c.status] ?? c.status}{c.skip_reason ? ` — ${c.skip_reason}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : selected ? (
          <dl className="lc-kv">
            <dt>Source</dt><dd>{selected.alert.service_name}</dd>
            <dt>Sévérité</dt><dd>{severityLabel(selected.severity)}</dd>
            <dt>Statut</dt>
            <dd>{selected.alert.state === 'ACTIVE'
              ? `Active — encore signalée par ${selected.alert.service_key === 'sonde' ? 'Sonde' : 'le service'}`
              : selected.alert.state === 'ACKNOWLEDGED' ? 'Vue — encore signalée par le service'
              : 'Résolue — n’est plus signalée'}</dd>
            <dt>Première observation</dt><dd>{fmtDate(selected.alert.first_seen_at)}</dd>
            <dt>Dernière observation</dt><dd>{fmtDate(selected.alert.last_seen_at)}</dd>
            <dt>Acquittement</dt>
            <dd>{selected.acknowledged
              ? `Vue par ${selected.acknowledgedBy ?? '—'} le ${fmtDate(selected.alert.acknowledged_at)}`
              : 'Pas encore vue'}</dd>
            {selected.note ? (<><dt>Précision</dt><dd>{selected.note}</dd></>) : null}
            <dt>Identifiant</dt>
            <dd><span className="lc-mono">{selected.alert.service_key}:{selected.alert.external_alert_id}</span></dd>
          </dl>
        ) : null}
      </Drawer>
    </main>
  )
}

export function ActivityCenterPage({ focusInstance = null }: { focusInstance?: string | null }) {
  return <ToastProvider><Inner focusInstance={focusInstance} /></ToastProvider>
}
